import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../commonApis/commonApis";

const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

export const useCreateOutletMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(`${url}/api/outlets`, payload);
      if (!res.success) throw new Error(res.message || "Failed to create outlet");
      return res.data;
    },
    onSuccess: (data) => {
      console.log(data, "Outlet Created");
      queryClient.invalidateQueries({ queryKey: ["outletsData"] });
    },
    onError: (error: any) => {
      console.error("❌ Outlet create failed:", error);
    },
  });
};

export const useFetchOutlets = (page: number, limit: number, search: string) => {
  return useQuery({
    queryKey: ["outlets", page, limit, search],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/outlets?page=${page}&limit=${limit}&search=${search}`,
        "get"
      );
      return res.data;
    },
  });
};

export const useFetchAllOutlets = () => {
  return useQuery({
    queryKey: ["allOutlets"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/outlets/all`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};


export const useFetchSingleOutlet = (id?: string) => {
  return useQuery({
    queryKey: ["singleOutlet", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await fetch3(`${url}/api/outlets/${id}`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id,
  });
};

export const useUpdateOutletMutation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(`${url}/api/outlets/${id}`, "PATCH", payload);
      if (!res.success) throw res.message || "Failed to update outlet";
      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Outlet updated successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["outletsData"] });
    },
    onError: (error: any) => {
      console.error("❌ Outlet update failed:", error.message);
    },
  });
};

export const useUpdateOutletStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { id: string; status: string }) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/outlets/status/${payload.id}`,
        "PATCH",
        payload
      );
      if (!res.success) throw res.message || "Failed to update status";
      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Outlet status updated:", data);
      queryClient.invalidateQueries({ queryKey: ["outletsData"] });
    },
    onError: (error: any) => {
      console.error("❌ Status update failed:", error.message);
    },
  });
};

export const useDeleteOutletMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res: ApiResponse = await fetch5(`${url}/api/outlets/${id}`, "DELETE");
      if (!res.success) throw res.message || "Failed to delete outlet";
      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Outlet deleted successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["outletsData"] });
    },
    onError: (error: any) => {
      console.error("❌ Outlet delete failed:", error.message);
    },
  });
};